import { Link } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import CapabilitiesSection from "../components/CapabilitiesSection";
import ReviewsSection from "../components/ReviewsSection";
import LocationsSection from "../components/LocationsSection";
import OurLocations from "../components/OurLocations";

const clientLogos = [
  { src: "/companies/telford-civil.png", alt: "Telford Civil" },
  { src: "/companies/sleekpixel.png", alt: "Sleekpixel" },
  { src: "/companies/sgs-platinum.jpeg", alt: "SGS" },
  { src: "/companies/js-architects.png", alt: "JS Architects" },
  { src: "/companies/aptec-plumbing.png", alt: "Aptec Plumbing" },
  { src: "/companies/alkaline-plumbing.png", alt: "Alkaline Plumbing" },
  { src: "/companies/instyle-plumbing.png", alt: "Instyle Plumbing" },
  { src: "/companies/niche-projects.png", alt: "Niche Projects" },
  { src: "/companies/online-pipe.png", alt: "Online Pipe & Cable Locating" },
  { src: "/companies/signsite.png", alt: "SignSite Group" },
  { src: "/companies/via.png", alt: "Visual Industries Australia" },
];

const slides = [
  { src: "/Plans1.jpg", alt: "Traffic management plan example" },
  { src: "/Plans2.jpg", alt: "Traffic guidance scheme" },
  { src: "/Plans3.jpg", alt: "Site traffic control plan" },
];

const SLIDE_MS = 5000;

function HeroSlideshow() {
  const [current, setCurrent] = useState(0);
  const [previous, setPrevious] = useState(0);
  const currentRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    slides.forEach((slide) => {
      const img = new Image();
      img.src = slide.src;
    });
  }, []);

  const armTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      const next = (currentRef.current + 1) % slides.length;
      setPrevious(currentRef.current);
      currentRef.current = next;
      setCurrent(next);
    }, SLIDE_MS);
  }, []);

  useEffect(() => {
    armTimer();
    return () => clearInterval(timerRef.current);
  }, [armTimer]);

  const goTo = (index) => {
    if (index === currentRef.current) return;
    setPrevious(currentRef.current);
    currentRef.current = index;
    setCurrent(index);
    armTimer();
  };

  return (
    <div>
      <style>{`
        @keyframes heroSlideLoad {
          from { transform: scaleY(0); }
          to { transform: scaleY(1); }
        }
        .hero-slide-load {
          transform-origin: top;
          animation: heroSlideLoad ${SLIDE_MS}ms linear forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-slide-load { animation: none; transform: scaleY(1); }
        }
      `}</style>
      <div className="flex items-stretch gap-4">
        <div className="relative min-w-0 flex-1">
          <div className="relative h-64 overflow-hidden rounded-2xl bg-[#121212] shadow-2xl shadow-black/40 ring-1 ring-white/10 sm:h-80 lg:h-[420px]">
            {slides.map((slide, i) => {
              const active = i === current;
              const underneath = i === previous && !active;
              return (
                <img
                  key={slide.src}
                  src={slide.src}
                  alt={active ? slide.alt : ""}
                  aria-hidden={active ? undefined : true}
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-in-out motion-reduce:transition-none ${
                    active || underneath ? "opacity-100" : "opacity-0"
                  }`}
                  style={{ zIndex: active ? 2 : underneath ? 1 : 0 }}
                />
              );
            })}
          </div>

          <div className="absolute bottom-4 left-4 z-10 rounded-2xl border border-white/10 bg-[#121212]/90 px-4 py-3 shadow-xl backdrop-blur-sm">
            <p className="text-xs font-bold uppercase tracking-widest text-white/50">Trusted by</p>
            <p className="text-lg font-extrabold text-white">100+ Clients</p>
          </div>
        </div>

        <div className="flex w-1.5 shrink-0 py-6" aria-hidden>
          <div className="relative h-full w-full overflow-hidden rounded-full bg-white/15">
            <div key={current} className="hero-slide-load absolute inset-0 rounded-full bg-orange-500" />
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-center gap-2 pr-6">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goTo(i)}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === current ? "w-6 bg-orange-500" : "w-2 bg-white/30 hover:bg-white/50"
            }`}
            aria-label={`Go to image ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <div>
      <section className="bg-[#121212] text-white">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-8 sm:px-6 md:pb-20 md:pt-10 lg:px-8 lg:pb-24">
          <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-16">
            <div>
              <p className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-orange-500">
                Traffic plans nationwide
              </p>
              <h1 className="mb-6 text-4xl font-extrabold uppercase tracking-tight text-white md:text-5xl lg:text-6xl">
                Professional Traffic Management Plans{" "}
                <span className="text-orange-500">Across Australia</span>
              </h1>
              <p className="mb-8 max-w-lg text-lg leading-relaxed text-white/70">
                Fast, compliant, and reliable traffic planning solutions for
                construction, events, and road works.
              </p>

              <dl className="mb-8 grid max-w-lg grid-cols-3 gap-3">
                <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-3">
                  <dt className="text-[10px] font-bold uppercase tracking-widest text-white/45">Quotes</dt>
                  <dd className="mt-1 text-sm font-extrabold text-white">30–60 min</dd>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-3">
                  <dt className="text-[10px] font-bold uppercase tracking-widest text-white/45">Coverage</dt>
                  <dd className="mt-1 text-sm font-extrabold text-white">8 states</dd>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-3">
                  <dt className="text-[10px] font-bold uppercase tracking-widest text-white/45">Support</dt>
                  <dd className="mt-1 text-sm font-extrabold text-white">24/7</dd>
                </div>
              </dl>

              <div className="flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-orange-600"
                >
                  Request a Quote
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                <a
                  href="tel:1300118170"
                  className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white transition hover:border-orange-400 hover:text-orange-500"
                >
                  <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  1300 118 170
                </a>
                <a
                  href="mailto:info@focustrafficplans.com.au"
                  className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white transition hover:border-orange-400 hover:text-orange-500"
                >
                  <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Email Us
                </a>
              </div>
            </div>

            <HeroSlideshow />
          </div>
        </div>
      </section>

      <section className="overflow-hidden bg-white py-7 text-center">
        <p className="mb-5 px-4 text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-[#5c5e64]">
          Trusted by contractors and developers across Australia
        </p>
        <style>{`
          @keyframes logoScroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-25%); }
          }
          .logo-scroll-track { animation: logoScroll 90s linear infinite; }
          .logo-scroll-track:hover { animation-play-state: paused; }
        `}</style>
        <div className="relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]">
          <div className="logo-scroll-track flex w-max items-center gap-12 md:gap-[72px]">
            {[0, 1, 2, 3].flatMap((copy) =>
              clientLogos.map((logo) => (
                <img
                  key={`${copy}-${logo.src}`}
                  src={logo.src}
                  alt={copy === 0 ? logo.alt : ""}
                  className="h-7 w-auto max-w-[110px] object-contain grayscale opacity-40 transition duration-300 hover:opacity-75 md:h-9 md:max-w-[140px]"
                />
              ))
            )}
          </div>
        </div>
      </section>

      <CapabilitiesSection />

      <ReviewsSection />

      <OurLocations />

      <LocationsSection />
    </div>
  );
}
